import { TherapyApi } from "@/api/therapy/TherapyApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useMedications(
  organizationId: string | undefined,
  opts?: { includeInactive?: boolean }
) {
  const q = useQuery({
    queryKey: [
      "therapy",
      "medications",
      organizationId ?? "",
      opts?.includeInactive ? "all" : "active",
    ],
    queryFn: () =>
      TherapyApi.listMedications(organizationId!, {
        includeInactive: opts?.includeInactive,
      }),
    enabled: !!organizationId,
  });
  return { medications: q.data ?? [], loading: q.isLoading, refetch: q.refetch };
}

export function useTreatments(
  organizationId: string | undefined,
  opts?: { includeInactive?: boolean }
) {
  const q = useQuery({
    queryKey: [
      "therapy",
      "treatments",
      organizationId ?? "",
      opts?.includeInactive ? "all" : "active",
    ],
    queryFn: () =>
      TherapyApi.listTreatments(organizationId!, {
        includeInactive: opts?.includeInactive,
      }),
    enabled: !!organizationId,
  });
  return { treatments: q.data ?? [], loading: q.isLoading, refetch: q.refetch };
}

export function useOpenCatalogReviews(organizationId: string | undefined) {
  const q = useQuery({
    queryKey: ["therapy", "reviews", organizationId ?? ""],
    queryFn: () => TherapyApi.listOpenReviews(organizationId!),
    enabled: !!organizationId,
  });
  return { reviews: q.data ?? [], loading: q.isLoading, refetch: q.refetch };
}

export function useDiagnosisTherapyRatings(organizationId: string | undefined) {
  const q = useQuery({
    queryKey: ["therapy", "ratings", organizationId ?? ""],
    queryFn: () => TherapyApi.listDiagnosisTherapyRatings(organizationId!),
    enabled: !!organizationId,
  });
  return { ratings: q.data ?? [], loading: q.isLoading, refetch: q.refetch };
}

export function useTherapySidebar(caseId: string | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ["therapy", "sidebar", caseId ?? ""],
    queryFn: () => TherapyApi.listSidebar(caseId!),
    enabled: !!caseId && enabled,
  });
  return {
    sidebar: q.data ?? { plans: [], hints: [] },
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function usePlanDues(caseId: string | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ["therapy", "dues", caseId ?? ""],
    queryFn: () => TherapyApi.previewDues(caseId!),
    enabled: !!caseId && enabled,
  });
  return { dues: q.data ?? [], loading: q.isLoading, refetch: q.refetch };
}

function invalidateCatalog(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  void qc.invalidateQueries({ queryKey: ["therapy", "medications", orgId] });
  void qc.invalidateQueries({ queryKey: ["therapy", "treatments", orgId] });
}

export function useUpsertMedication(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: TherapyApi.upsertMedication,
    onSuccess: () => invalidateCatalog(qc, organizationId ?? ""),
  });
}

export function useUpsertTreatment(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: TherapyApi.upsertTreatment,
    onSuccess: () => invalidateCatalog(qc, organizationId ?? ""),
  });
}

export function useSetMedicationActive(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      TherapyApi.setMedicationActive(id, isActive),
    onSuccess: () => invalidateCatalog(qc, organizationId ?? ""),
  });
}

export function useSetTreatmentActive(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      TherapyApi.setTreatmentActive(id, isActive),
    onSuccess: () => invalidateCatalog(qc, organizationId ?? ""),
  });
}

export function useResolveCatalogReview(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: TherapyApi.resolveReview,
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["therapy", "reviews", organizationId ?? ""],
      });
    },
  });
}

export function useUpsertDiagnosisTherapyRating(
  organizationId: string | undefined
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: TherapyApi.upsertDiagnosisTherapyRating,
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["therapy", "ratings", organizationId ?? ""],
      });
    },
  });
}

export function useDeleteDiagnosisTherapyRating(
  organizationId: string | undefined
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: TherapyApi.deleteDiagnosisTherapyRating,
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["therapy", "ratings", organizationId ?? ""],
      });
    },
  });
}
